/**
 * The explored design's highlighter (mockups/vestra-developers-docs-src/build.mjs, hlLine), as tokens:
 * comments, strings, keywords, numbers and calls, one line at a time. It colours text; it never changes it.
 */
import type { Door } from "../_content/card";

export type Lang = "ts" | "bash" | "python" | "json" | "yaml" | "html";

/** How each door's code is coloured (the samples' own files say the same: _lib/samples.ts). */
export const DOOR_LANG: Record<Door, Lang> = {
  ts: "ts",
  py: "python",
  cli: "bash",
  http: "bash",
  mcp: "json",
};
/** c a comment, s a string, k a keyword, n a number, f a call, p a JSON key. */
export type TokenKind = "c" | "s" | "k" | "n" | "f" | "p";
export interface Token {
  text: string;
  kind: TokenKind | null;
}

const KEYWORDS: Record<Lang, string> = {
  ts: "import|from|const|let|await|async|function|for|of|if|else|return|new|export|type|as|true|false|null|undefined|throw|try|catch|continue|break|declare",
  bash: "curl|export|npm|npx|node|cd|pip|python3",
  python:
    "import|from|def|for|in|if|return|with|as|await|async|True|False|None|else|elif|not|and|or|try|except|raise|class",
  json: "true|false|null",
  yaml: "true|false|null",
  html: "iframe",
};
const COMMENT: Record<Lang, string> = {
  ts: "\\/\\/.*$",
  bash: "#.*$",
  python: "#.*$",
  json: "(?!)",
  yaml: "#.*$",
  html: "<!--.*?-->",
};
const STRINGS =
  "'(?:[^'\\\\]|\\\\.)*'|\"(?:[^\"\\\\]|\\\\.)*\"|`(?:[^`\\\\]|\\\\.)*`";
const NUMBER = "-?\\b\\d+(?:\\.\\d+)?\\b";
const CALL = "([A-Za-z_][\\w.]*)(?=\\()";

function pattern(lang: Lang): RegExp {
  return new RegExp(
    `(${COMMENT[lang]})|(${STRINGS})|\\b(${KEYWORDS[lang]})\\b|(${NUMBER})|${CALL}`,
    "g"
  );
}

/** One line's tokens. Joined back together they are the line, character for character. */
export function highlightLine(line: string, lang: Lang): Token[] {
  const out: Token[] = [];
  const re = pattern(lang);
  let last = 0;
  for (let m = re.exec(line); m !== null; m = re.exec(line)) {
    if (m.index > last)
      out.push({ text: line.slice(last, m.index), kind: null });
    const key =
      lang === "json" &&
      m[2] !== undefined &&
      /^\s*:/.test(line.slice(m.index + m[0].length));
    const kind: TokenKind =
      m[1] !== undefined
        ? "c"
        : m[2] !== undefined
          ? key
            ? "p"
            : "s"
          : m[3] !== undefined
            ? "k"
            : m[4] !== undefined
              ? "n"
              : "f";
    out.push({ text: m[0], kind });
    last = m.index + m[0].length;
  }
  if (last < line.length) out.push({ text: line.slice(last), kind: null });
  return out;
}

export function highlight(code: string, lang: Lang): Token[][] {
  return code.split("\n").map((line) => highlightLine(line, lang));
}
